import { isNil } from 'lodash'
import React, {
    useCallback, useEffect, useState
} from 'react'
import BasePagination from './BasePagination'

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100, 250, 500]
export const DEFAULT_ITEMS_PER_PAGE = ITEMS_PER_PAGE_OPTIONS[0]

type OnPageChangeProps = {
    page: number
    itemsPerPage: number
}

type Props = {
    page: number
    totalItems: number
    onPageChange: (props: OnPageChangeProps) => Promise<void>
    disabled?: boolean
    itemPerPage?: number,
    itemsPerPageOpts?: number[]
}

/**
 * A wrapper of {@code <BasePagination/>} to trigger API calls. Pagination only allows to go to next / previous page,
 * because we use graphql specs: https://graphql.org/learn/pagination/#end-of-list-counts-and-connections.
 */
const Pagination = (props: Props) => {
    const {
        page, totalItems, onPageChange, disabled, itemPerPage, itemsPerPageOpts
    } = props
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [loading, setLoading] = useState<boolean>(false)

    const loadPage = useCallback(async (onPageChangeProps: OnPageChangeProps): Promise<boolean> => {
        try {
            setLoading(true)
            await onPageChange(onPageChangeProps)
            setCurrentPage(onPageChangeProps.page)
            return true
        } finally {
            setLoading(false)
        }
        return false
    }, [onPageChange])

    const handlePageChange = useCallback(async (newPage, itemsPerPage): Promise<boolean> => loadPage({
        page: newPage,
        itemsPerPage,
    }), [loadPage])

    // init loading
    useEffect(() => {
        (async () => isNil(totalItems) && await loadPage({
            page: 1,
            itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        }))()
    }, [loadPage, totalItems])

    // init loading
    useEffect(() => {
        setCurrentPage(page)
    }, [page])

    return (
        <BasePagination
            currentPage={currentPage}
            itemsPerPage={itemPerPage || DEFAULT_ITEMS_PER_PAGE}
            itemsPerPageOpts={itemsPerPageOpts || ITEMS_PER_PAGE_OPTIONS}
            disabled={loading || disabled}
            totalItems={totalItems || 0}
            onPageChange={handlePageChange}
        />
    )
}

export default Pagination
